// Top bar (HANDOFF §1): search 40 px (max 460) with the «/» chip · sync pill
// (offline / conflict — nothing when synced: the normal state is silent) ·
// neutral «موعد جديد» · bell · business chip/switcher.
//
// Phone (<768, owner addition A): the bar keeps search + «موعد جديد» (+ bell
// and the business mark as 48 px targets). The sync status does NOT fit in
// the bar at its longest («بلا اتصال · آخر مزامنة ٤:١٩ · ٣ مستندات محلية» is
// ~280 px of nowrap text), so on phones it renders as a full-width STRIP
// directly under the bar instead of a pill inside it — same copy, same
// tones, and the search field keeps ≥ 120 px (shell.css).

import { forwardRef, useEffect, useState, type FormEvent, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, Check, ChevronDown, CloudOff, Plus, RefreshCw, Search, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/Button';
import { Pill } from '@/components/Pill';
import { businessApi, erpApi } from '@/lib/api';
import { arNumber, formatDate, formatTime, localDocsLabel } from '@/lib/format';
import { useLayout } from '@/lib/layout';
import { listQueue, replayQueue, useSyncStatus, type QueueItem, type SyncStatus } from '@/lib/offline-queue';
import { openBookingPanel } from '@/features/booking/public';
import { HotelSearch, openReservationPanel } from '@/features/hotel/public';
import { statementHref } from '@/features/accounts/documents/lib/links';
import { PropertySwitcher } from '@/features/hotel/components/PropertySwitcher';
import { useErp } from './erp-context';
import { moduleGate, passes, type GateContext } from './gates';
import { MODULES } from './modules';
import { SidePanel } from './SidePanel';

/** «٣ مستندات بانتظار المزامنة» (13 §29) with Arabic count agreement. */
export function pendingDocsLabel(n: number): string {
  const count = n === 1 ? 'مستند واحد' : n === 2 ? 'مستندان' : n <= 10 ? `${arNumber(n)} مستندات` : `${arNumber(n)} مستنداً`;
  return `${count} بانتظار المزامنة`;
}

/**
 * Top-bar sync pill copy (boards today/board--offline, accounts/sync--conflict,
 * accounts/offline--local-queue; 13 §29). Null = render nothing (synced is silent).
 */
export function syncPillText(s: SyncStatus): { tone: 'warn' | 'err'; text: string } | null {
  if (s.conflicts > 0) {
    return { tone: 'err', text: `${arNumber(s.conflicts)} تعارض · ${arNumber(s.pending)} بانتظار المزامنة` };
  }
  if (s.offline) {
    const parts = ['بلا اتصال'];
    if (s.lastSyncAt) parts.push(`آخر مزامنة ${formatTime(s.lastSyncAt, { suffix: false })}`);
    if (s.pending > 0) parts.push(localDocsLabel(s.pending));
    return { tone: 'warn', text: parts.join(' · ') };
  }
  // Online with documents still waiting (a replay in flight or retrying).
  if (s.pending > 0) return { tone: 'warn', text: pendingDocsLabel(s.pending) };
  return null;
}

/** The queued document's customer (invoice + receipt payloads both carry these). */
function queueCustomer(item: QueueItem): { id: string | null; name: string } {
  const p = (item.payload ?? {}) as { customer_id?: unknown; customer_name?: unknown };
  return {
    id: typeof p.customer_id === 'string' && p.customer_id ? p.customer_id : null,
    name: typeof p.customer_name === 'string' ? p.customer_name : '',
  };
}

/** Where a queued document opens: its statement with the conflict (or local) panel. */
export function queueItemHref(item: QueueItem): string | null {
  const c = queueCustomer(item);
  if (!c.id) return null;
  return statementHref(c.id, { panel: item.state === 'conflict' ? 'conflict' : 'local', doc: item.client_id });
}

const DOC_WORD: Record<QueueItem['type'], string> = { invoice: 'فاتورة', receipt_voucher: 'سند قبض' };

/** This business's unsynced queue items, oldest first; reloads when the counts move. */
function useQueueItems(businessId: string, status: SyncStatus, enabled: boolean): QueueItem[] | null {
  const [items, setItems] = useState<QueueItem[] | null>(null);
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    listQueue(businessId)
      .then((all) => {
        if (alive) setItems(all.filter((i) => i.state !== 'synced'));
      })
      .catch(() => {
        if (alive) setItems([]);
      });
    return () => {
      alive = false;
    };
  }, [businessId, status.pending, status.conflicts, status.offline, enabled]);
  return items;
}

/**
 * The local queue panel (opened from the sync pill when there is not exactly
 * one conflict): every unsynced document with its state, a link to it, a retry
 * (online), and «الطابور المحلي فارغ.» when nothing is waiting.
 */
export function SyncQueuePanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { me } = useErp();
  const navigate = useNavigate();
  const status = useSyncStatus();
  const items = useQueueItems(me.business.id, status, open);
  const qc = useQueryClient();
  const [retrying, setRetrying] = useState(false);
  const retry = async () => {
    setRetrying(true);
    try {
      const r = await replayQueue();
      if (r.synced > 0 || r.conflicts > 0) void qc.invalidateQueries();
    } finally {
      setRetrying(false);
    }
  };
  const waiting = (items ?? []).some((i) => i.state === 'pending' || i.state === 'syncing');
  return (
    <SidePanel
      open={open}
      title="المزامنة"
      onClose={onClose}
      data-testid="sync-queue-panel"
      footer={
        waiting && !status.offline ? (
          <Button variant="line" block icon={RefreshCw} loading={retrying} onClick={() => void retry()} data-testid="sync-queue-retry">
            أعد المحاولة
          </Button>
        ) : undefined
      }
    >
      {items && items.length === 0 ? (
        <p className="e-queue-empty" data-testid="sync-queue-empty">
          الطابور المحلي فارغ.
        </p>
      ) : (
        <ul className="e-queue-list" aria-label="المزامنة">
          {(items ?? []).map((i) => {
            const c = queueCustomer(i);
            const href = queueItemHref(i);
            const state = i.state === 'conflict' ? 'conflict' : status.offline ? 'local' : 'pending';
            return (
              <li key={i.client_id} className="e-queue-item" data-state={i.state} data-testid="sync-queue-item">
                <span className="e-queue-doc">
                  <span>
                    {DOC_WORD[i.type]} <span className="e-isolate">{i.number_text ?? i.temp_number}</span>
                  </span>
                  {c.name && <small>{c.name}</small>}
                </span>
                {state === 'conflict' ? (
                  <Pill tone="err">تعارض</Pill>
                ) : state === 'local' ? (
                  <Pill tone="warn">محلي · رقم مؤقت</Pill>
                ) : (
                  <Pill tone="warn">بانتظار المزامنة</Pill>
                )}
                {href && (
                  <Button
                    size="sm"
                    variant="line"
                    onClick={() => {
                      onClose();
                      navigate(href);
                    }}
                  >
                    افتح
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </SidePanel>
  );
}

/**
 * Pill / strip → action: exactly one conflict opens that conflict over its
 * statement (accounts/sync--conflict); anything else opens the queue panel.
 */
function useSyncPillAction(status: SyncStatus): { onOpen: () => void; panel: JSX.Element } {
  const { me } = useErp();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const onOpen = () => {
    if (status.conflicts === 1) {
      void listQueue(me.business.id).then((all) => {
        const conflicts = all.filter((i) => i.state === 'conflict');
        const href = conflicts.length === 1 ? queueItemHref(conflicts[0]) : null;
        if (href) navigate(href);
        else setOpen(true);
      });
      return;
    }
    setOpen(true);
  };
  return { onOpen, panel: <SyncQueuePanel open={open} onClose={() => setOpen(false)} /> };
}

/** In-bar sync pill (≥ 768): a button that opens the conflict or the queue. */
export function SyncStatusPill() {
  const status = useSyncStatus();
  const p = syncPillText(status);
  const { onOpen, panel } = useSyncPillAction(status);
  return (
    <>
      {p && (
        <button type="button" className="e-sync-btn" onClick={onOpen} aria-haspopup="dialog" data-testid="sync-pill-button">
          <Pill tone={p.tone} size="status" data-testid="sync-pill">
            {p.text}
          </Pill>
        </button>
      )}
      {panel}
    </>
  );
}

/** Phone variant: a full-width strip under the top bar with the same copy and action. */
export function SyncStatusStrip() {
  const status = useSyncStatus();
  const p = syncPillText(status);
  const { onOpen, panel } = useSyncPillAction(status);
  const Icon = p?.tone === 'err' ? TriangleAlert : CloudOff;
  return (
    <>
      {p && (
        <button type="button" className="e-sync-strip" data-tone={p.tone} data-testid="sync-strip" onClick={onOpen} aria-haspopup="dialog">
          <Icon aria-hidden="true" />
          <span role="status">{p.text}</span>
        </button>
      )}
      {panel}
    </>
  );
}

function moduleVisible(key: string, ctx: GateContext): boolean {
  const m = MODULES.find((x) => x.key === key);
  return !!m && passes(moduleGate(m), ctx);
}

interface Notif {
  id: string;
  title: string;
  body: string;
  created_at: string;
  read_at?: string | null;
}

function NotificationsBell() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const count = useQuery({
    queryKey: ['business', 'notifications', 'count'],
    queryFn: () => businessApi.get<{ unread: number }>('/v1/business/notifications/count'),
    staleTime: 60_000,
    retry: 0,
  });
  const list = useQuery({
    queryKey: ['business', 'notifications', 'latest'],
    // The popover is a glance, not the feed: the latest 20 only (the BFF trims on `limit`).
    queryFn: () => businessApi.get<{ notifications: Notif[] }>('/v1/business/notifications', { limit: 20 }),
    enabled: open,
    retry: 0,
  });
  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    const onDown = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onDown); };
  }, [open]);
  const readAll = async () => {
    try {
      await businessApi.post('/v1/business/notifications/read-all');
    } finally {
      void qc.invalidateQueries({ queryKey: ['business', 'notifications'] });
    }
  };
  const unread = count.data?.unread ?? 0;
  const markRead = async (id: string) => {
    try {
      await businessApi.post(`/v1/business/notifications/${id}/read`);
    } finally {
      void qc.invalidateQueries({ queryKey: ['business', 'notifications'] });
    }
  };
  const items = list.data?.notifications ?? [];
  return (
    <div style={{ position: 'relative' }} ref={panelRef}>
      <button type="button" className="e-iconbtn" aria-label="الإشعارات" aria-expanded={open} onClick={() => setOpen((o) => !o)} data-testid="bell">
        <Bell aria-hidden="true" />
        {unread > 0 && <span className="e-dot" data-testid="bell-dot" />}
      </button>
      {open && (
        <div className="e-popover e-popover--notifs" role="menu" style={{ width: 'min(340px, 90vw)' }} data-testid="notifs-popover">
          <div className="e-popover-head">
            <span className="e-popover-label" style={{ padding: 0 }}>الإشعارات</span>
            {items.some((n) => !n.read_at) && (
              <button type="button" className="e-popover-readall" onClick={() => void readAll()} data-testid="notifs-read-all">
                قراءة الكل
              </button>
            )}
          </div>
          <div className="e-popover-scroll" data-testid="notifs-scroll">
          {list.isLoading && <div className="muted e-popover-note">…</div>}
          {!list.isLoading && items.length === 0 && <div className="muted e-popover-note">لا إشعارات.</div>}
          {items.map((n) => (
            <button key={n.id} type="button" role="menuitem" className="e-popover-item" style={{ alignItems: 'flex-start', paddingBlock: 8 }} onClick={() => void markRead(n.id)}>
              <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontWeight: n.read_at ? 600 : 700 }}>{n.title}</span>
                <span className="muted" style={{ fontWeight: 500, fontSize: 13 }}>
                  {n.body}
                </span>
                <span className="muted" style={{ fontWeight: 500, fontSize: 12 }}>
                  {formatDate(n.created_at)} · {formatTime(n.created_at)}
                </span>
              </span>
              {n.read_at && <Check aria-hidden="true" width={16} height={16} />}
            </button>
          ))}
          </div>
        </div>
      )}
    </div>
  );
}

/** erp-core GET /v1/erp/branches item (internal/branches.Response). */
export interface BranchItem {
  id: string;
  name: string;
  is_main: boolean;
  active: boolean;
  /** The cloud mirror appends the branch city (the hotel switcher's «كربلاء · ٥٣»). */
  city?: string;
}

export const BRANCHES_QUERY_KEY = ['erp', 'shell', 'branches'] as const;

/** Active branches of the business — only asked with erp.branches on. */
function useBranches(enabled: boolean) {
  return useQuery({
    queryKey: BRANCHES_QUERY_KEY,
    queryFn: () => erpApi.get<{ items?: BranchItem[] }>('/branches'),
    enabled,
    staleTime: 5 * 60_000,
    retry: 0,
    select: (r) => (r?.items ?? []).filter((b) => b.active),
  });
}

/**
 * The business chip: the business (switcher when there are several
 * memberships) and, with erp.branches and ≥ 2 active branches, the active
 * branch under it (board settings/branches caption: the switcher lives in
 * the business chip). A hotel's chip is instead the one-line breadcrumb
 * «فندق قصر الزوّار › كربلاء» (board 27) — the business › the property's
 * CITY (`me.branch.city`, the name until erp-core sends it) — whenever a
 * property is in sight, one or many, erp.branches or not; the property's
 * name is the switcher's. A branch choice rides on every erp-core call as
 * X-Branch-Id.
 */
function BusinessChip() {
  const { me, switchBusiness, switchBranch, flag, termProfile } = useErp();
  const [open, setOpen] = useState(false);
  const branchesOn = flag('erp.branches');
  const branches = useBranches(branchesOn).data ?? [];
  const multiBranch = branchesOn && branches.length > 1;
  const multiBiz = me.memberships.length > 1;
  const activeBranchId = me.branch?.id ?? null;
  const breadcrumb = termProfile === 'hotel' && !!me.branch;
  const place = me.branch ? me.branch.city || me.branch.name : '';
  // A hotel with several properties gets the board-27 property switcher (name over «المدينة · الغرف»).
  const hotelSwitcher = termProfile === 'hotel' && multiBranch;
  const content = (
    <>
      <span className="e-bizchip-mark" aria-hidden="true">
        {me.business.name.trim().charAt(0)}
      </span>
      {breadcrumb ? (
        <span className="e-bizchip-text" data-breadcrumb="true">
          <span className="e-bizchip-name">{me.business.name}</span>
          <span className="e-bizchip-sep" aria-hidden="true">
            {' › '}
          </span>
          <span className="e-bizchip-branch" data-testid="biz-chip-branch">
            {place}
          </span>
        </span>
      ) : (
        <span className="e-bizchip-text">
          <span className="e-bizchip-name">{me.business.name}</span>
          {multiBranch && me.branch && (
            <span className="e-bizchip-branch" data-testid="biz-chip-branch">
              {me.branch.name}
            </span>
          )}
        </span>
      )}
      {(multiBiz || multiBranch) && <ChevronDown aria-hidden="true" width={16} height={16} />}
    </>
  );
  const label = breadcrumb ? `${me.business.name} › ${place}` : multiBranch && me.branch ? `${me.business.name} · ${me.branch.name}` : me.business.name;
  if (!multiBiz && !multiBranch) {
    return (
      <div className="e-bizchip" data-testid="biz-chip" aria-label={label}>
        {content}
      </div>
    );
  }
  return (
    <div style={{ position: 'relative' }}>
      <button type="button" className="e-bizchip" aria-haspopup="listbox" aria-expanded={open} aria-label={label} onClick={() => setOpen((o) => !o)} data-testid="biz-chip">
        {content}
      </button>
      {open && (
        <div className={hotelSwitcher ? 'e-popover e-popover--property' : 'e-popover'} data-testid="biz-switcher">
          {multiBiz && (
            <div role="listbox" aria-label="النشاط">
              {multiBranch && <div className="e-popover-label">النشاط</div>}
              {me.memberships.map((m) => (
                <button
                  key={m.business_id}
                  type="button"
                  role="option"
                  aria-selected={m.business_id === me.business.id}
                  className="e-popover-item"
                  onClick={() => {
                    setOpen(false);
                    if (m.business_id !== me.business.id) switchBusiness(m.business_id);
                  }}
                >
                  {m.business_name}
                  {m.business_id === me.business.id && <Check aria-hidden="true" width={16} height={16} />}
                </button>
              ))}
            </div>
          )}
          {multiBiz && multiBranch && <div className="e-popover-sep" role="separator" />}
          {hotelSwitcher && (
            <PropertySwitcher
              branches={branches}
              activeBranchId={activeBranchId}
              onPick={(b) => {
                setOpen(false);
                if (b.id !== activeBranchId) switchBranch(b.is_main ? null : b.id);
              }}
              onClose={() => setOpen(false)}
            />
          )}
          {multiBranch && !hotelSwitcher && (
            <div role="listbox" aria-label="الفرع" data-testid="branch-switcher">
              {multiBiz && <div className="e-popover-label">الفرع</div>}
              {branches.map((b) => (
                <button
                  key={b.id}
                  type="button"
                  role="option"
                  aria-selected={b.id === activeBranchId}
                  className="e-popover-item"
                  onClick={() => {
                    setOpen(false);
                    // The main branch is the server default: no header needed.
                    if (b.id !== activeBranchId) switchBranch(b.is_main ? null : b.id);
                  }}
                >
                  {b.name}
                  {b.id === activeBranchId && <Check aria-hidden="true" width={16} height={16} />}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export interface TopBarProps {
  onSearch?: (q: string) => void;
}

export const TopBar = forwardRef<HTMLInputElement, TopBarProps>(function TopBar({ onSearch }, searchRef) {
  const { gateContext, mode, termProfile } = useErp();
  const navigate = useNavigate();
  // A hotel session (board 27): the reservations search and «حجز جديد» take the
  // places of the customers search and «موعد جديد».
  const hotel = termProfile === 'hotel' && moduleVisible('hotelReservations', gateContext);
  const status = useSyncStatus();
  const phone = useLayout() === 'phone';
  const [q, setQ] = useState('');
  const canBook = moduleVisible('booking', gateContext);
  const canSearch = moduleVisible('customers', gateContext);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const s = q.trim();
    if (!s) return;
    if (onSearch) onSearch(s);
    else if (canSearch) navigate(`/customers?q=${encodeURIComponent(s)}`);
  };

  return (
    <>
      <header className="e-topbar" data-testid="top-bar">
        {hotel && <HotelSearch ref={searchRef} />}
        {!hotel && canSearch && (
          <form className="e-search" role="search" onSubmit={submit}>
            <Search aria-hidden="true" width={18} height={18} />
            <input ref={searchRef} value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث باسم أو رقم هاتف" aria-label="ابحث باسم أو رقم هاتف" />
            <kbd className="e-kbd" aria-hidden="true">
              /
            </kbd>
          </form>
        )}
        <span className="e-topbar-spacer" />
        {!phone && <SyncStatusPill />}
        {hotel && (
          <Button variant="line" icon={Plus} onClick={() => openReservationPanel()} disabled={status.offline} data-testid="new-reservation-button">
            حجز جديد
          </Button>
        )}
        {!hotel && canBook && (
          <Button variant="line" icon={Plus} onClick={() => openBookingPanel()} disabled={status.offline} data-testid="new-appointment">
            موعد جديد
          </Button>
        )}
        {mode === 'online' && <NotificationsBell />}
        <BusinessChip />
      </header>
      {phone && <SyncStatusStrip />}
    </>
  );
});
